import { useEffect } from "react";
import { BackButton, Button } from "../../components";
import { ContactServiceSteps } from "./types";
import { useNavigate, useSearchParams } from "react-router-dom";
import { checkAllKeysHaveValue, classNames, navigateTo } from "../../helper";
import { PiCheckBold } from "react-icons/pi";
import RouteNames from "../../routes/type";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CustomerDetails from "./CustomerDetails";
import PurchaseDetails from "./PurchaseDetails";
import queryString from "query-string";
import SubmitContactService from "./SubmitContactService";
import { connect, useDispatch } from "react-redux";
import { fetchOptions } from "../../store/user/actions";
import DamageDetails from "./DamageDetails";
import {
  createDeskTicket,
  updateCSState,
} from "../../store/contact-service/actions";
import ReviewContactService from "./ReviewContactService";
import { RootState } from "../../store/root-reducer";
import Loader from "../../components/Loader";
import { isScopeLoading } from "../../store/scope-loading/helpers";
import { ContactServiceScope } from "../../store/contact-service/types";
import toast from "react-hot-toast";
import { UserScope } from "../../store/user/types";

function ContactService({ data, loading }) {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formMethods = useForm({
    defaultValues: {
      purchaseDetails: data.purchaseDetails,
      customerDetails: data.customerDetails,
      claimItemDamage: data.claimItemDamage,
    },
  });
  const { handleSubmit, getValues } = formMethods;
  const { t } = useTranslation();
  const stepId = parseInt(searchParams.get("step") || "");
  const tempValidationId = searchParams.get("id") || "";

  const currentStep = ContactServiceSteps.find(
    (item) => item.step_num === stepId
  );

  useEffect(() => {
    if (
      (!stepId && !/^[0-9]*$/.test(`${stepId}`)) ||
      (!stepId && !/^[0-9]*$/.test(`${stepId}`)) ||
      stepId > 5 ||
      stepId < 1 ||
      !tempValidationId
    ) {
      navigate(`/${RouteNames.CONTACT_SERVICE}?step=1`, { replace: true });
    }
    return () => {};
  }, [navigate, stepId, tempValidationId]);

  useEffect(() => {
    dispatch(fetchOptions());
    return () => {};
  }, [dispatch]);

  const navigateToNextStep = (step, otherPrams = {}) => {
    const parsed = queryString.parse(window.location.search);
    window.scrollTo(0, 0);
    navigateTo(
      `/${RouteNames.CONTACT_SERVICE}?${queryString.stringify({
        ...parsed,
        ...otherPrams,
        step: step ?? stepId + 1,
      })}`
    );
  };

  const handleNext = () => {
    switch (stepId) {
      case 1:
        const customerDetails = getValues("customerDetails");
        dispatch(
          updateCSState({
            customerDetails: {
              name: customerDetails?.name,
              email: customerDetails?.email,
              phone: customerDetails?.phone,
              address: customerDetails?.address,
              city: customerDetails?.city,
              state: customerDetails?.state,
              zip_code: customerDetails?.zip_code,
            },
          })
        );
        navigateToNextStep(stepId + 1, {
          id: new Date().getTime(),
        });
        break;

      case 2:
        const purchaseDetails = getValues("purchaseDetails");
        dispatch(
          updateCSState({
            purchaseDetails: {
              invoice: purchaseDetails?.invoice,
              date_of_purchase: purchaseDetails?.date_of_purchase,
              store_name: purchaseDetails?.store_name,
              store_phone: purchaseDetails?.store_phone,
              store_address: purchaseDetails?.store_address,
              store_city: purchaseDetails?.store_city,
              store_state: purchaseDetails?.store_state,
              were_any_items_exchanged:
                purchaseDetails?.were_any_items_exchanged,
              store_zip: purchaseDetails?.store_zip,
            },
          })
        );
        navigateToNextStep(stepId + 1);
        break;

      case 3:
        const claimItemDamage = getValues("claimItemDamage");
        dispatch(
          updateCSState({
            claimItemDamage,
          })
        );
        navigateToNextStep(stepId + 1);
        break;

      case 4:
        if (checkAllKeysHaveValue(data)) {
          dispatch(createDeskTicket());
        } else {
          toast.error(
            "Please complete all the missing data and try submitting again.",
            {
              position: "bottom-center",
              id: "form_invalid",
            }
          );
        }
        break;

      default:
        break;
    }
  };

  const renderStepComponent = () => {
    switch (stepId) {
      case 1:
        return <CustomerDetails key="stepId1" />;
      case 2:
        return <PurchaseDetails key="stepId2" />;
      case 3:
        return <DamageDetails key="stepId3" />;
      case 4:
        return <ReviewContactService key="stepId4" />;
      default:
        return null;
    }
  };

  if (stepId === 5) {
    return <SubmitContactService />;
  }

  return (
    <div className="px-4 bg-white">
      <div className="mb-8 mt-6 flex justify-between items-center">
        <BackButton
          onClick={() => {
            if (stepId === 1) {
              navigate("/");
            } else {
              navigateToNextStep(stepId - 1);
            }
          }}
          text={stepId === 1 ? t("Home") : t("Back")}
        />
        <div className="flex flex-row gap-2">
          {ContactServiceSteps.map(({ step_num: step }) => {
            const isCompleted = step < stepId;
            const isDraft = step > stepId;
            return (
              <button
                disabled
                key={step}
                className={classNames(
                  "size-7 flex rounded-full justify-center items-center text-base",
                  isDraft
                    ? "bg-[#009CAA] bg-opacity-15 text-[#009CAA]"
                    : "bg-[#009CAA] text-white"
                )}
              >
                {isCompleted ? <PiCheckBold size={16} color="white" /> : step}
              </button>
            );
          })}
        </div>
      </div>
      <div className="mb-2">
        <h3 className="text-2xl font-semibold">{t(currentStep?.title)}</h3>
      </div>
      <form className="grid gap-y-4" onSubmit={handleSubmit(handleNext)}>
        <Loader isLoading={loading} />
        <FormProvider {...formMethods}>{renderStepComponent()}</FormProvider>
        <Button submit className="my-4">
          {stepId === 4 ? t("Submit") : t("Next")}
        </Button>
      </form>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  data: state?.contactService || {},
  loading:
    isScopeLoading(state.scopeLoading, ContactServiceScope) ||
    isScopeLoading(state.scopeLoading, UserScope),
});

export default connect(mapStateToProps)(ContactService);
